import { Key } from 'antd/es/table/interface';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pagination, Row, Transfer, TransferProps, Typography } from 'components';
import { CustomTransferList } from './customTransferList';
import './role_permission.scss';

const { Text } = Typography;

export type TransferItem = {
  key: string;
  title: string;
  isTitle?: boolean;
  groupKey?: string;
};
export type MappedKeys = {
  [key: string]: Key[];
};

interface IData {
  content: Array<any>;
  totalElements: number;
  totalPages?: number;
}

interface ITitle {
  left: string;
  right: string;
}

interface TransferComponentProps {
  data: IData;
  initialData: MappedKeys | Record<string, any>[];
  titles: ITitle;
  targetKeys: Key[];
  isLoading: boolean;
  onChange: (targetKeys: Key[], mappedKeys: MappedKeys) => void;
  onSearch: (value: string) => void;
  handlePageChange: (page: number) => void;
  isRole?: boolean;
}

export const TransferComponent: React.FC<TransferComponentProps> = React.memo((props) => {
  const {
    data = {} as IData,
    initialData,
    titles,
    targetKeys,
    isLoading,
    onChange,
    onSearch,
    handlePageChange,
    isRole = false,
  } = props;
  const { content = [], totalElements, totalPages } = data;
  const [treeData, setTreeData] = useState<TransferItem[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectKeys, setSelectKeys] = useState<Key[]>([]);
  const [leftSideData, setLeftSideData] = useState<TransferItem[]>([]);
  const [rightSideData, setRightSideData] = useState<TransferItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [leftCurrentPage, setLeftCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const leftBoxRef = useRef<HTMLDivElement>(null);
  const rightBoxRef = useRef<HTMLDivElement>(null);

  // Helper: Transform content (API data) into TransferItem[] for non-role and role cases.
  const transformContent = useCallback((): TransferItem[] => {
    if (!content?.[0]) return [];
    if (isRole) {
      return ((content[0]?.roleManagements as string[]) || []).map((role: any) => ({
        key: role.id,
        title: role.name,
        isTitle: false,
      }));
    }
    return Object.entries(content[0]).flatMap(([key, values]) => [
      { key, title: key, isTitle: true },
      ...(values as string[]).map((perm) => ({
        groupKey: key,
        key: perm,
        title: perm,
        isTitle: false,
      })),
    ]);
  }, [content, isRole]);

  // useEffect for processing API content.
  useEffect(() => {
    if (!content?.[0] || isLoading) return;

    const newData = transformContent();

    const rightSideKeys = new Set(rightSideData.map((item) => item.key));

    const newLeftSide = newData.filter((item) => {
      if (!item.isTitle) {
        return !rightSideKeys.has(item.key);
      } else {
        const groupChildren = newData.filter((child) => child.groupKey === item.key);
        if (groupChildren.length === 0) return true;
        return !groupChildren.every((child) => rightSideKeys.has(child.key));
      }
    });
    setLeftSideData(newLeftSide);

    setTreeData((prevData) => {
      const existingKeys = new Set(prevData.map((item) => item.key));
      return [...prevData, ...newData.filter((item) => !existingKeys.has(item.key))];
    });
  }, [content, isRole, isLoading, rightSideData, transformContent]);

  // useEffect for processing initialData.
  useEffect(() => {
    if (!initialData) return;

    const transformInitial = (): TransferItem[] => {
      if (isRole && Array.isArray(initialData)) {
        return initialData.map((_data: any) => ({
          key: _data.id,
          title: _data.name,
          isTitle: false,
        }));
      }
      return Object.entries(initialData).flatMap(([key, values]) => [
        { key, title: key, isTitle: true },
        ...(values as string[]).map((perm) => ({
          groupKey: key,
          key: perm,
          title: perm,
          isTitle: false,
        })),
      ]);
    };
    const rightSideEditData = transformInitial();

    setLeftSideData((prevState) => {
      const rightSideKeys = new Set(rightSideEditData.map((item) => item.key));
      return prevState.filter((item) => !rightSideKeys.has(item.key) || item.isTitle);
    });

    setRightSideData(rightSideEditData);
    setTreeData((prevData) => {
      const existingKeys = new Set(prevData.map((item) => item.key));
      return [...prevData, ...rightSideEditData.filter((item) => !existingKeys.has(item.key))];
    });
  }, [initialData, isRole]);

  const currentRightSideData = useMemo(() => {
    let relatedItems = rightSideData.filter((item) => !item.isTitle);

    if (searchValue) {
      relatedItems = relatedItems.filter(
        (_item) =>
          _item.title.toLowerCase().includes(searchValue.toLowerCase()) ||
          _item.title.toLowerCase().startsWith(searchValue.toLowerCase())
      );
    }
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    const paginatedRelatedItems: TransferItem[] = relatedItems.slice(start, end);

    const result: TransferItem[] = [];
    const addedGroups = new Set<string>();

    paginatedRelatedItems.forEach((item) => {
      if (item.groupKey && !addedGroups.has(item.groupKey)) {
        const groupTitle = rightSideData.find((g) => g.key === item.groupKey && g.isTitle);
        if (groupTitle) {
          result.push(groupTitle);
          addedGroups.add(item.groupKey);
        }
      }
      result.push(item);
    });

    return result;
  }, [currentPage, pageSize, rightSideData, searchValue]);

  const filterOption = useCallback((inputValue: string, item: TransferItem) => {
    return String(item.title || '')
      .toLowerCase()
      .includes(inputValue.toLowerCase());
  }, []);

  const onPageChange = useCallback(
    (page: number) => {
      if (leftBoxRef.current) {
        leftBoxRef.current.scrollTop = 0;
      }
      setLeftCurrentPage(page);
      handlePageChange(page);
    },
    [handlePageChange]
  );

  // --- TRANSFER TO RIGHT ---
  // When moving items from left to right, we want the new items to appear at the top
  // (within their group). We remove the moved items from leftSideData and then prepend them
  // (with their header if needed) to the rightSideData.
  const transferToRight = useCallback(
    (moveKeys: Key[]) => {
      const updatedLeft = leftSideData.filter((item) => {
        if (!item.isTitle) {
          return !moveKeys.includes(item.key);
        } else {
          const groupChildren = leftSideData.filter((child) => child.groupKey === item.key);
          return !groupChildren.every((child) => moveKeys.includes(child.key));
        }
      });
      if (!updatedLeft.length && (totalPages as number) > leftCurrentPage) {
        onPageChange(leftCurrentPage + 1);
      } else {
        setLeftSideData(updatedLeft);
      }

      let remainingRight = rightSideData.filter((item) => !moveKeys.includes(item.key));

      const itemsToAdd = moveKeys
        .map((key) => treeData.find((item) => item.key === key))
        .filter(Boolean) as TransferItem[];

      const newGroups = new Map<string, TransferItem[]>();
      const newNonGroupItems: TransferItem[] = [];
      itemsToAdd.forEach((item) => {
        if (item.groupKey) {
          if (!newGroups.has(item.groupKey)) {
            newGroups.set(item.groupKey, []);
          }
          newGroups.get(item.groupKey)!.push(item);
        } else {
          newNonGroupItems.push(item);
        }
      });

      const groupsToPrepend: TransferItem[] = [];
      newGroups.forEach((newItems, groupKey) => {
        remainingRight = remainingRight.filter((item) => item.key !== groupKey && item.groupKey !== groupKey);

        const header = treeData.find((t) => t.key === groupKey && t.isTitle) || {
          key: groupKey,
          title: groupKey,
          isTitle: true,
        };

        const oldGroupItems = rightSideData.filter(
          (item) => item.groupKey === groupKey && !moveKeys.includes(item.key)
        );
        const sortByTreeIndex = (a: TransferItem, b: TransferItem) =>
          treeData.findIndex((t) => t.key === a.key) - treeData.findIndex((t) => t.key === b.key);
        newItems.sort(sortByTreeIndex);
        oldGroupItems.sort(sortByTreeIndex);

        groupsToPrepend.push(header, ...newItems, ...oldGroupItems);
      });

      newNonGroupItems.sort(
        (a, b) => treeData.findIndex((t) => t.key === a.key) - treeData.findIndex((t) => t.key === b.key)
      );

      const newRightList = [...groupsToPrepend, ...newNonGroupItems, ...remainingRight];

      setRightSideData(newRightList);
    },
    [leftSideData, rightSideData, treeData, leftCurrentPage, totalPages, onPageChange]
  );

  // --- TRANSFER TO LEFT ---
  // When moving items from right to left, we want the new (returned) items to be added to the
  // current page (i.e. appended to the existing left list) without changing the visible page.
  // Here we simply append the returned items (and, if needed, their group headers) to leftSideData.
  const transferToLeft = useCallback(
    (moveKeys: Key[]) => {
      const updatedRight = rightSideData.filter((item) => {
        if (item.isTitle) {
          const groupChildren = rightSideData.filter((subItem) => subItem.groupKey === item.key);
          return groupChildren.some((subItem) => !moveKeys.includes(subItem.key));
        }
        return !moveKeys.includes(item.key);
      });

      const titleCount = updatedRight.filter((_item) => !_item.isTitle).length;
      if ((currentPage - 1) * 10 >= titleCount) {
        setCurrentPage((prevState) => Math.max(prevState - 1, 1));
      }
      setRightSideData(updatedRight);

      const updatedLeft = [...leftSideData];
      const itemsToReturn = moveKeys
        .map((key) => treeData.find((item) => item.key === key))
        .filter(Boolean) as TransferItem[];

      itemsToReturn.forEach((item) => {
        if (item.groupKey) {
          const headerExists = updatedLeft.some((i) => i.key === item.groupKey && i.isTitle);
          if (!headerExists) {
            updatedLeft.push({ key: item.groupKey, title: item.groupKey, isTitle: true });
          }
        }
        updatedLeft.push(item);
      });
      setLeftSideData(updatedLeft);
    },
    [leftSideData, rightSideData, treeData, currentPage]
  );

  // Consolidated handleChange calls the appropriate helper.
  const handleChange = useCallback(
    (targetKeys: Key[], direction: 'left' | 'right', moveKeys: Key[]) => {
      if (direction === 'right') {
        transferToRight(moveKeys);
        if (rightBoxRef.current) {
          rightBoxRef.current.scrollTop = 0;
        }
      } else {
        transferToLeft(moveKeys);
      }
      const mappedKeys: MappedKeys = {};
      targetKeys.forEach((key) => {
        const item = treeData.find((item) => item.key === key);
        if (!item) return;
        if (item.groupKey) {
          if (!mappedKeys[item.groupKey]) {
            mappedKeys[item.groupKey] = [];
          }
          mappedKeys[item.groupKey].push(key);
        }
      });
      onChange(targetKeys, mappedKeys);
      setSelectKeys([]);
    },
    [treeData, onChange, transferToRight, transferToLeft]
  );

  const handleCheckboxChange = useCallback((key: React.Key, checked: boolean) => {
    setSelectKeys((prevKeys) => (checked ? [...prevKeys, key] : prevKeys.filter((k) => k !== key)));
  }, []);
  const handleTargetPageChange = useCallback(
    (page: number, pageSize: number) => {
      if (rightBoxRef.current) {
        rightBoxRef.current.scrollTop = 0;
      }
      setCurrentPage(page);
      setPageSize(pageSize);
    },
    [rightBoxRef]
  );
  const handleSearch = useCallback(
    (direction: string, value: string) => {
      if (direction === 'left') {
        setLeftCurrentPage(1);
        onSearch(value);
      } else {
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
        setSearchValue(value?.toLowerCase());
      }
    },
    [onSearch, currentPage]
  );
  const renderFooter: TransferProps['footer'] = (_, info) => {
    if (info?.direction === 'left') {
      return (
        <Pagination
          align={'center'}
          onChange={onPageChange}
          current={leftCurrentPage}
          className={'margin-t-12'}
          showSizeChanger={false}
          simple
          size="small"
          pageSize={10}
          total={totalElements}
        />
      );
    } else if (targetKeys?.length) {
      return (
        <Pagination
          align={'center'}
          current={currentPage}
          onChange={handleTargetPageChange}
          className={'margin-t-12'}
          showSizeChanger={false}
          simple
          size="small"
          pageSize={10}
          total={
            rightSideData.filter(
              (_item) =>
                (_item.title.toLowerCase().includes(searchValue.toLowerCase()) ||
                  _item.title.toLowerCase().startsWith(searchValue.toLowerCase())) &&
                !_item.isTitle
            ).length
          }
        />
      );
    }
  };
  const renderTransferList = useMemo(
    () => (listProps: any) => (
      <CustomTransferList
        direction={listProps.direction}
        selectKeys={selectKeys}
        isLoading={isLoading}
        leftBoxRef={leftBoxRef}
        rightBoxRef={rightBoxRef}
        leftSideData={leftSideData}
        rightSideData={currentRightSideData}
        handleCheckboxChange={handleCheckboxChange}
      />
    ),
    [leftSideData, currentRightSideData, handleCheckboxChange, isLoading, selectKeys]
  );
  return (
    <Transfer
      showSearch
      onChange={handleChange}
      showSelectAll={false}
      onSearch={handleSearch}
      dataSource={treeData}
      selectedKeys={selectKeys}
      filterOption={filterOption}
      targetKeys={targetKeys}
      className="role-permission-transfer"
      locale={{
        searchPlaceholder: 'Search',
      }}
      listStyle={{
        width: 290,
        padding: 16,
        backgroundColor: '#ffffff',
        height: 370,
      }}
      footer={renderFooter}
      titles={[
        <Row align={'middle'} justify={'space-between'} key={'1'}>
          <Text className={'font-weight-600'}>{titles.left}</Text>
          <Text className={'font-weight-500'}>
            {targetKeys?.length || 0} / {totalElements}
          </Text>
        </Row>,
        <Row align={'middle'} justify={'space-between'} key={'2'}>
          <Text className={'font-weight-600'}>{titles.right}</Text>
          <Text className={'font-weight-500'}>{targetKeys?.length}</Text>
        </Row>,
      ]}
    >
      {renderTransferList}
    </Transfer>
  );
});
